import { Box, Chip, Paper, Typography } from "@mui/material";
import { DataGridPro } from "@mui/x-data-grid-pro";
import PropTypes from "prop-types";

import { formatNumber } from "@/features/shared";
import { ProductVariantNameCell } from "@/features/ui";

import { getLineCostPerUnit } from "../../stock-adjustment-file";
import {
  StockAdjustmentTypeEnum,
  StockAdjustmentTypeLabel,
} from "../../stock-adjustment-form";

// Small documents grow to fit; a large one (an opening balance runs to thousands of lines) scrolls
// inside a fixed-height, virtualized grid instead of rendering every row.
const AUTO_HEIGHT_MAX_LINES = 50;
const GRID_HEIGHT = 640;

const getType = (quantity) =>
  quantity < 0
    ? StockAdjustmentTypeEnum.DEDUCTION
    : StockAdjustmentTypeEnum.ADDITION;

export const StockAdjustmentProductVariantsSection = ({
  stockAdjustmentProductVariants,
  warehouse,
}) => {
  const rows = stockAdjustmentProductVariants.map((line) => ({
    ...line,
    type: getType(line.quantity),
    displayQuantity: Math.abs(line.quantity),
    costPerUnit: getLineCostPerUnit(line),
  }));

  return (
    <Paper sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{
        mb: 2
      }}>
        Penyesuaian Stok
      </Typography>
      <Box
        sx={
          rows.length > AUTO_HEIGHT_MAX_LINES
            ? { height: GRID_HEIGHT, width: "100%" }
            : { display: "flex", flexDirection: "column" }
        }
      >
        {/* DataGridPro: the free grid shows at most 100 rows per page (and with its footer hidden,
            only those 100). The Pro grid scrolls every line, virtualized. */}
        <DataGridPro
          rows={rows}
          autoHeight={rows.length <= AUTO_HEIGHT_MAX_LINES}
          getRowHeight={() => "auto"}
          getEstimatedRowHeight={() => 64}
          disableRowSelectionOnClick
          hideFooter={rows.length <= AUTO_HEIGHT_MAX_LINES}
          hideFooterSelectedRowCount
          showToolbar={rows.length > AUTO_HEIGHT_MAX_LINES}
          localeText={{
            noRowsLabel: "Tidak ada baris",
            footerTotalRows: "Total baris:",
            toolbarQuickFilterPlaceholder: "Cari nama atau SKU",
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: "name", sort: "asc" }],
            },
          }}
          columns={[
            {
              field: "name",
              headerName: "Nama",
              flex: 3,
              renderCell: ({ row }) => (
                <ProductVariantNameCell
                  name={row.name}
                  sku={row.sku}
                  linkToStock
                  warehouseId={warehouse?.id}
                  warehouseName={warehouse?.name}
                />
              ),
            },
            {
              field: "type",
              headerName: "Tipe",
              width: 140,
              renderCell: ({ row }) => (
                <Chip
                  size="small"
                  color={
                    row.type === StockAdjustmentTypeEnum.ADDITION
                      ? "success"
                      : "warning"
                  }
                  label={StockAdjustmentTypeLabel[row.type]}
                />
              ),
            },
            {
              field: "displayQuantity",
              headerName: "Qty",
              flex: 1,
              type: "number",
              valueFormatter: (value) => formatNumber(value),
            },
            {
              field: "unitOfMeasurement",
              headerName: "Unit",
              width: 80,
              sortable: false,
              valueGetter: (value, row) =>
                row.product?.unitOfMeasurement?.name ?? "-",
            },
            {
              field: "costPerUnit",
              headerName: "Biaya Satuan (Rp)",
              flex: 1.2,
              type: "number",
              renderCell: ({ row }) =>
                row.type === StockAdjustmentTypeEnum.DEDUCTION
                  ? "-"
                  : formatNumber(row.costPerUnit || 0),
            },
            {
              field: "note",
              headerName: "Catatan",
              flex: 1.5,
              sortable: false,
              valueGetter: (value) => value || "-",
            },
          ]}
        />
      </Box>
    </Paper>
  );
};

StockAdjustmentProductVariantsSection.propTypes = {
  stockAdjustmentProductVariants: PropTypes.array.isRequired,
  warehouse: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
  }),
};
